/**
 * Deployment Configuration
 *
 * Detects deployment mode and provides appropriate configuration
 * for cloud (Supabase) or on-premises (Docker) deployments.
 */

// Deployment modes
export const DEPLOYMENT_MODE = {
  CLOUD: 'cloud',
  ONPREM: 'onprem'
};

/**
 * Get current deployment mode from environment
 * @returns {string} 'cloud' or 'onprem'
 */
export const getDeploymentMode = () => {
  const mode = process.env.REACT_APP_DEPLOYMENT_MODE || DEPLOYMENT_MODE.CLOUD;
  return mode.toLowerCase();
};

/**
 * Check if running in cloud deployment mode
 * @returns {boolean}
 */
export const isCloudDeployment = () => {
  return getDeploymentMode() === DEPLOYMENT_MODE.CLOUD;
};

/**
 * Check if running in on-premises deployment mode
 * @returns {boolean}
 */
export const isOnPremDeployment = () => {
  return getDeploymentMode() === DEPLOYMENT_MODE.ONPREM;
};

/**
 * Get Core API base URL
 * @returns {string} Base URL for Core API
 */
export const getCoreApiUrl = () => {
  // For on-prem, use relative path (nginx proxy)
  if (isOnPremDeployment()) {
    return process.env.REACT_APP_CORE_SERVICE_URL || '';
  }
  // For cloud: use configured URL, fall back to localhost only in dev
  // (production builds must set REACT_APP_CORE_SERVICE_URL; if missing,
  // return empty so requests fall through to the /api proxy)
  if (process.env.REACT_APP_CORE_SERVICE_URL) {
    return process.env.REACT_APP_CORE_SERVICE_URL;
  }
  return process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';
};

/**
 * Whether the page's own origin proxies /api/* to the core service.
 *
 * Every server that hosts the webapp forwards /api/* to the core service:
 * Express (cloud, server.js), nginx (on-prem) and the React dev server
 * (src/setupProxy.js). The proxy is only a *fallback* when the webapp is
 * configured to call the core service directly; with no direct URL the
 * requests already go through it.
 * @returns {boolean}
 */
export const hasSameOriginApiProxy = () => Boolean(getCoreApiUrl());

/**
 * Get deployment configuration
 * @returns {Object} Configuration object
 */
export const getConfig = () => {
  const mode = getDeploymentMode();

  return {
    mode,
    isCloud: mode === DEPLOYMENT_MODE.CLOUD,
    isOnPrem: mode === DEPLOYMENT_MODE.ONPREM,
    coreApiUrl: getCoreApiUrl(),
    supabaseUrl: process.env.REACT_APP_SUPABASE_URL,
    supabaseAnonKey: process.env.REACT_APP_SUPABASE_ANON_KEY
  };
};

// Log deployment mode on load (for debugging)
if (process.env.NODE_ENV === 'development') {
  console.log('Deployment Mode:', getDeploymentMode());
  console.log('Core API URL:', getCoreApiUrl());
}
